export type ReceiptGuess = {
  billCents: number | null
  tipCents: number | null
  totalCents: number | null
  candidates: number[]
  rawText: string
}

const MONEY_RE = /\$?\s*(\d{1,4}(?:,\d{3})*\.\d{2})/g

function parseMoneyToken(token: string): number | null {
  const value = Number.parseFloat(token.replace(/,/g, ''))
  if (!Number.isFinite(value) || value <= 0 || value > 5000) return null
  return Math.round(value * 100)
}

function uniqueCents(values: Array<number | null | undefined>): number[] {
  const seen = new Set<number>()
  const out: number[] = []
  for (const value of values) {
    if (value == null || seen.has(value)) continue
    seen.add(value)
    out.push(value)
  }
  return out
}

function lineHas(line: string, pattern: RegExp): boolean {
  return pattern.test(line)
}

/** Suggested tip grids (18% / Tip / Total columns) — not the check total. */
export function isTipSuggestionLine(line: string): boolean {
  if (/suggested\s*tip/i.test(line)) return true
  if (/^\s*\d{1,2}\s*%\s/.test(line)) return true
  if (/\d{1,2}\s*%\s+\$?\d+\.\d{2}/.test(line)) return true
  if (/\bpercent\b/i.test(line) && /\btip\b/i.test(line) && /\btotal\b/i.test(line)) return true
  return false
}

function tipSuggestionStartIndex(lines: string[]): number {
  const idx = lines.findIndex((line) => isTipSuggestionLine(line))
  return idx >= 0 ? idx : lines.length
}

function extractLabeledInRange(
  lines: string[],
  label: RegExp,
  endExclusive: number,
): number | null {
  const slice = lines.slice(0, endExclusive)
  for (let i = slice.length - 1; i >= 0; i -= 1) {
    const line = slice[i]
    if (isTipSuggestionLine(line)) continue
    if (!lineHas(line, label)) continue
    const matches = [...line.matchAll(MONEY_RE)]
    if (matches.length === 0) {
      const next = slice[i + 1]
      if (next && !isTipSuggestionLine(next)) {
        const nextMatch = [...next.matchAll(MONEY_RE)]
        if (nextMatch[0]) return parseMoneyToken(nextMatch[0][1])
      }
      continue
    }
    const cents = parseMoneyToken(matches[matches.length - 1][1])
    if (cents == null) continue
    if (/\d{1,2}\s*%/.test(line)) continue
    return cents
  }
  return null
}

/** Check total line — not subtotal, not tip-table "Total" column. */
function extractCheckTotal(lines: string[], endExclusive: number): number | null {
  const slice = lines.slice(0, endExclusive)
  for (let i = slice.length - 1; i >= 0; i -= 1) {
    const line = slice[i]
    if (isTipSuggestionLine(line)) continue
    if (/\bsub\s*-?\s*total\b/i.test(line)) continue
    if (/\bgrand\s*total\b/i.test(line)) continue
    if (/\bpay\s*with\b/i.test(line)) continue
    if (!/\btotal\b/i.test(line)) continue
    if (/\d{1,2}\s*%/.test(line)) continue
    const matches = [...line.matchAll(MONEY_RE)]
    if (matches.length === 0) continue
    return parseMoneyToken(matches[matches.length - 1][1])
  }
  return null
}

function sumLineItemAmounts(lines: string[], endExclusive: number): number | null {
  let sum = 0
  let count = 0
  for (const line of lines.slice(0, endExclusive)) {
    if (isTipSuggestionLine(line)) break
    if (
      /\b(sub\s*-?\s*total|sales\s*tax|\btotal\b|amount\s*due|balance\s*due|tip|gratuity|pay\s*with|change\s*due)\b/i.test(
        line,
      )
    ) {
      continue
    }
    const matches = [...line.matchAll(MONEY_RE)]
    if (matches.length !== 1) continue
    const cents = parseMoneyToken(matches[0][1])
    if (cents == null || cents < 100) continue
    sum += cents
    count += 1
  }
  if (count === 0) return null
  return sum
}

function allAmountsInRange(text: string, endExclusive: number, lines: string[]): number[] {
  const slice = lines.slice(0, endExclusive).join('\n')
  return uniqueCents([...slice.matchAll(MONEY_RE)].map((match) => parseMoneyToken(match[1])))
}

/**
 * Pull a pre-tip bill out of messy OCR. Prefers labeled TOTAL / AMOUNT DUE
 * over SUBTOTAL, ignores suggested-tip grids, and can sum line items when the
 * total block was cropped out.
 */
export function parseReceipt(text: string): ReceiptGuess {
  const rawText = text.replace(/\u00a0/g, ' ')
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  const tipBlockStart = tipSuggestionStartIndex(lines)
  const tipCents = extractLabeledInRange(lines, /\b(tip|gratuity|grat)\b/i, tipBlockStart)
  const subtotalCents = extractLabeledInRange(lines, /\bsub\s*-?\s*total\b/i, tipBlockStart)
  const taxCents = extractLabeledInRange(lines, /\b(sales\s*)?tax\b/i, tipBlockStart)
  const amountDueCents = extractLabeledInRange(
    lines,
    /\b(amount\s*due|balance\s*due)\b/i,
    tipBlockStart,
  )
  const grandTotalCents = extractLabeledInRange(lines, /\bgrand\s*total\b/i, tipBlockStart)
  const checkTotalCents = extractCheckTotal(lines, tipBlockStart)

  const lineItemSum = sumLineItemAmounts(lines, tipBlockStart)
  const subtotalPlusTax =
    subtotalCents != null && taxCents != null ? subtotalCents + taxCents : null
  const itemsPlusTax = lineItemSum != null && taxCents != null ? lineItemSum + taxCents : null

  const candidates = uniqueCents([
    amountDueCents,
    checkTotalCents,
    grandTotalCents,
    subtotalPlusTax,
    itemsPlusTax,
    subtotalCents,
    lineItemSum,
    tipCents,
    ...allAmountsInRange(rawText, tipBlockStart, lines),
  ]).sort((a, b) => b - a)

  let billCents =
    amountDueCents ??
    checkTotalCents ??
    grandTotalCents ??
    subtotalPlusTax ??
    itemsPlusTax ??
    subtotalCents ??
    lineItemSum ??
    null

  // If TOTAL already includes tip, peel it off so the calculator can re-tip.
  if (billCents != null && tipCents != null && billCents > tipCents) {
    const maybePreTip = billCents - tipCents
    if (subtotalCents != null && Math.abs(maybePreTip - subtotalCents) <= 2) {
      billCents = subtotalCents
    } else if (subtotalPlusTax != null && Math.abs(maybePreTip - subtotalPlusTax) <= 2) {
      billCents = subtotalPlusTax
    } else if (grandTotalCents != null && billCents === grandTotalCents) {
      billCents = maybePreTip
    }
  }

  if (billCents == null && candidates.length > 0) {
    const beforeTips = allAmountsInRange(rawText, tipBlockStart, lines)
    billCents = Math.max(...beforeTips, ...candidates)
  }

  const alternates = filterAlternateCandidates(billCents, candidates, tipBlockStart, lines)

  return {
    billCents,
    tipCents,
    totalCents: amountDueCents ?? checkTotalCents ?? grandTotalCents,
    candidates: alternates,
    rawText,
  }
}

/** Other amounts OCR saw — drop tip-table totals and tiny junk. */
function filterAlternateCandidates(
  billCents: number | null,
  candidates: number[],
  tipBlockStart: number,
  lines: string[],
): number[] {
  const tipTableAmounts = new Set<number>()
  for (const line of lines.slice(tipBlockStart)) {
    for (const match of line.matchAll(MONEY_RE)) {
      const cents = parseMoneyToken(match[1])
      if (cents != null) tipTableAmounts.add(cents)
    }
  }

  let list = candidates.filter((c) => !tipTableAmounts.has(c) || c === billCents)
  if (list.length === 0) list = candidates.slice(0, 12)
  list = list.slice(0, 12)

  if (list.length <= 1) return list.slice(0, 6)
  const anchor = billCents ?? list[0]
  if (anchor <= 0) return list.slice(0, 6)
  const floor = Math.max(500, Math.round(anchor * 0.15))
  const ceiling = Math.round(anchor * 2.5)
  const filtered = list.filter((c) => c >= floor && c <= ceiling)
  return (filtered.length > 0 ? filtered : list).slice(0, 6)
}
